import { ref, computed, nextTick } from 'vue';
import type { Box } from '~/composables/use-quadrants';

export type PdfQuadrant = {
  page: number;
} & Box;

export type SimpleQuadrant = {
  id: string | number;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type LogoConfig = {
  x: number;
  y: number;
  width: number;
  height: number;
  image: HTMLImageElement;
  opacity: number;
  quadrantId: string | number;
  page?: number;
};

export type LogoState = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  quadrantId: string | number;
  page: number;
};

export type CustomerLogo = {
  fileUrl: string;
  accountLogoId: string;
};

export type KonvaNode = {
  getStage(): any;
  getLayer(): any;
  x(): number;
  y(): number;
  width(): number;
  height(): number;
  scaleX(): number;
  scaleY(): number;
  position(pos: { x: number; y: number }): void;
  getClientRect(): { x: number; y: number; width: number; height: number };
  nodes(nodes?: any[]): any[];
  forceUpdate(): void;
  batchDraw(): void;
  on(event: string, handler: (event: Event) => void): void;
  off(event: string): void;
  id(): string;
  name(): string;
};

export type CustomizationEvent = {
  action: 'auto-save' | 'reset';
  data?: {
    logos: LogoConfig[];
    currentPage: number;
    logoId: string;
    isPersonalized: boolean;
  };
  page?: number;
  contentId?: number;
  fileId?: number;
  imageData?: string;
};

export type PdfQuadrantsOptions = {
  onQuadrantsChanged?: () => void;
  onCustomizationChange?: (event: CustomizationEvent) => void;
  onPersonalizationChange?: (data: {
    isPersonalized: boolean;
    customization?: any;
  }) => void;
  onAutoSave?: () => void;
  onTransformerUpdate?: () => void;
};

export type LoadQuadrantsOptions = {
  propsQuadrants: Array<{
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    page: number;
  }>;
  currentPage: number;
  canvasWidth: number;
  canvasHeight: number;
  originalPageWidth: number;
  originalPageHeight: number;
};

const useQuadrantEvents = (options: PdfQuadrantsOptions) => {
  const emit = (eventType: string, data?: any) => {
    switch (eventType) {
      case 'customization-change': {
        options.onCustomizationChange?.(data);
        break;
      }
      case 'personalization-change': {
        options.onPersonalizationChange?.(data);
        break;
      }
      case 'auto-save': {
        options.onAutoSave?.();
        break;
      }
      case 'quadrants-changed': {
        options.onQuadrantsChanged?.();
        break;
      }
      case 'transformer-update': {
        options.onTransformerUpdate?.();
        break;
      }
      default: {
        break;
      }
    }
  };

  return { emit };
};

const useQuadrantState = () => {
  const simpleQuadrants = ref<SimpleQuadrant[]>([]);
  const quadrants = ref<PdfQuadrant[]>([]);
  const quadrantsScaled = ref(false);

  const selectedLogoRef = ref<KonvaNode | null>(null);
  const isLogoSelected = ref(false);
  const quadrantOccupancy = ref<Map<string, KonvaNode>>(new Map());
  const logoHomeQuadrants = ref<Map<KonvaNode, SimpleQuadrant>>(new Map());

  const customerLogo = ref<CustomerLogo | null>(null);
  const logoImageElement = ref<HTMLImageElement | null>(null);
  const logoImageLoaded = ref(false);

  const logoStatesPerPage = ref<Map<number, Map<string, LogoState>>>(new Map());
  const isReestablishingBounds = ref(false);

  const getLogoImageConfig = (quadrant: SimpleQuadrant): LogoConfig | null => {
    if (!quadrant || typeof quadrant !== 'object') {
      return null;
    }

    if (
      !customerLogo.value ||
      !logoImageElement.value ||
      !logoImageLoaded.value
    ) {
      return null;
    }

    const img = logoImageElement.value;
    const logoWidth = img.naturalWidth || img.width;
    const logoHeight = img.naturalHeight || img.height;

    if (logoWidth === 0 || logoHeight === 0) {
      return null;
    }

    if (
      !quadrant.width ||
      !quadrant.height ||
      quadrant.width <= 0 ||
      quadrant.height <= 0
    ) {
      return null;
    }

    const logoAspectRatio = logoWidth / logoHeight;
    const quadrantAspectRatio = quadrant.width / quadrant.height;

    let fittedWidth: number;
    let fittedHeight: number;

    if (logoAspectRatio > quadrantAspectRatio) {
      fittedWidth = quadrant.width * 0.8;
      fittedHeight = fittedWidth / logoAspectRatio;
    } else {
      fittedHeight = quadrant.height * 0.8;
      fittedWidth = fittedHeight * logoAspectRatio;
    }

    const x = quadrant.x + (quadrant.width - fittedWidth) / 2;
    const y = quadrant.y + (quadrant.height - fittedHeight) / 2;

    return {
      x,
      y,
      width: fittedWidth,
      height: fittedHeight,
      image: img,
      opacity: 0.9,
      quadrantId: quadrant.id,
    };
  };

  const logoConfigs = computed((): LogoConfig[] => {
    if (
      !customerLogo.value ||
      !logoImageLoaded.value ||
      !logoImageElement.value ||
      simpleQuadrants.value.length === 0
    ) {
      return [];
    }

    const configs = simpleQuadrants.value
      .map((quadrant) => {
        const config = getLogoImageConfig(quadrant);
        const result = config ? { ...config, quadrantId: quadrant.id } : null;
        return result;
      })
      .filter((config): config is LogoConfig => config !== null);

    return configs;
  });

  const selectedLogoQuadrant = computed(() => {
    if (!selectedLogoRef.value) return null;
    return logoHomeQuadrants.value.get(selectedLogoRef.value);
  });

  return {
    simpleQuadrants,
    quadrants,
    quadrantsScaled,
    selectedLogoRef,
    isLogoSelected,
    quadrantOccupancy,
    logoHomeQuadrants,
    customerLogo,
    logoImageElement,
    logoImageLoaded,
    logoConfigs,
    selectedLogoQuadrant,
    logoStatesPerPage,
    isReestablishingBounds,
    getLogoImageConfig,
  };
};

const useQuadrantInteractions = (
  state: ReturnType<typeof useQuadrantState>,
  events: ReturnType<typeof useQuadrantEvents>,
) => {
  const updateQuadrantOccupancy = (
    image: KonvaNode,
    quadrant: SimpleQuadrant,
  ) => {
    if (!image || !quadrant) return;

    for (const [
      quadrantId,
      occupant,
    ] of state.quadrantOccupancy.value.entries()) {
      if (occupant === image) {
        state.quadrantOccupancy.value.delete(quadrantId);
      }
    }

    state.quadrantOccupancy.value.set(quadrant.id.toString(), image);
    state.logoHomeQuadrants.value.set(image, quadrant);
  };

  const getLogoHomeQuadrant = (image: KonvaNode) => {
    return state.logoHomeQuadrants.value.get(image);
  };

  const isQuadrantOccupied = (
    quadrant: SimpleQuadrant,
    excludeImage?: KonvaNode,
  ) => {
    if (!quadrant) return false;
    const occupant = state.quadrantOccupancy.value.get(quadrant.id.toString());
    return occupant && occupant !== excludeImage;
  };

  const handleLogoClick = (target: KonvaNode) => {
    state.selectedLogoRef.value = target;
    state.isLogoSelected.value = true;

    let homeQuadrant = getLogoHomeQuadrant(target);
    if (homeQuadrant) {
      updateQuadrantOccupancy(target, homeQuadrant);
    } else {
      const logoId = target.id();
      const quadrantId = logoId.replace('logo-quadrant-', '');
      homeQuadrant = state.simpleQuadrants.value.find(
        (q) => q.id.toString() === quadrantId,
      );

      if (homeQuadrant) {
        updateQuadrantOccupancy(target, homeQuadrant);
      }
    }

    nextTick(() => {
      events.emit('transformer-update');
    });

    events.emit('auto-save');
  };

  const handleLogoDrag = (target: KonvaNode) => {
    const logoId = target.id();
    const quadrantId = logoId.replace('logo-quadrant-', '');

    const quadrant = state.simpleQuadrants.value.find(
      (q) => q.id.toString() === quadrantId,
    );

    if (quadrant) {
      updateQuadrantOccupancy(target, quadrant);
      events.emit('auto-save');
    }
  };

  const handleLogoRemove = (quadrantId: string | number) => {
    const numericQuadrantId =
      typeof quadrantId === 'string'
        ? Number.parseInt(quadrantId, 10)
        : quadrantId;

    const logoConfigs = state.logoConfigs.value;
    const configIndex = logoConfigs.findIndex(
      (config) => config.quadrantId === numericQuadrantId,
    );

    if (configIndex !== -1) {
      logoConfigs.splice(configIndex, 1);
      state.quadrantOccupancy.value.delete(quadrantId.toString());

      const selectedElement = state.selectedLogoRef.value;
      if (selectedElement) {
        const selectedQuadrant = getLogoHomeQuadrant(selectedElement);
        if (selectedQuadrant && selectedQuadrant.id === numericQuadrantId) {
          state.selectedLogoRef.value = null;
          state.isLogoSelected.value = false;
        }
      }
      events.emit('auto-save');
    }
  };

  const handleStageClick = (e: any) => {
    const { target } = e;
    if (target === e.target.getStage()) {
      state.isLogoSelected.value = false;
      state.selectedLogoRef.value = null;
    }
  };

  const establishLogoHomeQuadrant = (logoNode: KonvaNode) => {
    if (!logoNode) return;
    if (getLogoHomeQuadrant(logoNode)) return;

    const logoId = logoNode.id();
    const quadrantId = logoId.replace('logo-quadrant-', '');
    const homeQuadrant = state.simpleQuadrants.value.find(
      (q) => q.id.toString() === quadrantId,
    );

    if (homeQuadrant) {
      updateQuadrantOccupancy(logoNode, homeQuadrant);
    }
  };

  const reestablishAllLogoHomeQuadrants = () => {
    if (state.isReestablishingBounds.value) {
      return;
    }

    state.isReestablishingBounds.value = true;

    try {
      for (const [
        quadrantId,
        logoNode,
      ] of state.quadrantOccupancy.value.entries()) {
        if (logoNode) {
          const existingHomeQuadrant =
            state.logoHomeQuadrants.value.get(logoNode);
          if (existingHomeQuadrant) {
            continue;
          }

          const homeQuadrant = state.simpleQuadrants.value.find(
            (q) => q.id.toString() === quadrantId,
          );
          if (homeQuadrant) {
            state.logoHomeQuadrants.value.set(logoNode, homeQuadrant);
          }
        }
      }
    } finally {
      state.isReestablishingBounds.value = false;
    }
  };

  const createDragBoundFunc = (quadrantId: string | number) => {
    return (pos: { x: number; y: number }) => {
      const currentQuadrant = state.simpleQuadrants.value.find(
        (q) => q.id.toString() === quadrantId.toString(),
      );

      if (!currentQuadrant) return pos;

      const logoElement = state.selectedLogoRef.value;
      if (!logoElement) return pos;

      const logoWidth = logoElement.width() * logoElement.scaleX();
      const logoHeight = logoElement.height() * logoElement.scaleY();

      const buffer = 0.1;
      const maxX =
        currentQuadrant.x + currentQuadrant.width - logoWidth - buffer;
      const maxY =
        currentQuadrant.y + currentQuadrant.height - logoHeight - buffer;

      const constrainedPos = {
        x: Math.max(currentQuadrant.x + buffer, Math.min(maxX, pos.x)),
        y: Math.max(currentQuadrant.y + buffer, Math.min(maxY, pos.y)),
      };

      return constrainedPos;
    };
  };

  const createResizeBoundFunc = () => {
    return (oldValue: any, newValue: any) => {
      const image = state.selectedLogoRef.value;
      if (!image) {
        return oldValue;
      }

      const homeQuadrant = getLogoHomeQuadrant(image);
      if (!homeQuadrant) {
        return oldValue;
      }

      const minSize = 10;
      if (newValue.width < minSize || newValue.height < minSize) {
        return oldValue;
      }

      const currentX = oldValue.x;
      const currentY = oldValue.y;
      const availableWidth = homeQuadrant.x + homeQuadrant.width - currentX;
      const availableHeight = homeQuadrant.y + homeQuadrant.height - currentY;

      const fitsInAvailableSpace =
        newValue.width <= availableWidth && newValue.height <= availableHeight;
      const fitsInQuadrant =
        newValue.width <= homeQuadrant.width &&
        newValue.height <= homeQuadrant.height;

      if (!fitsInQuadrant) {
        return oldValue;
      }

      if (!fitsInAvailableSpace) {
        return oldValue;
      }

      const maxX = homeQuadrant.x + homeQuadrant.width - newValue.width;
      const maxY = homeQuadrant.y + homeQuadrant.height - newValue.height;

      const constrainedValue = {
        ...newValue,
        x: Math.max(homeQuadrant.x, Math.min(maxX, newValue.x)),
        y: Math.max(homeQuadrant.y, Math.min(maxY, newValue.y)),
        width: Math.abs(newValue.width),
        height: Math.abs(newValue.height),
      };

      return constrainedValue;
    };
  };

  return {
    updateQuadrantOccupancy,
    getLogoHomeQuadrant,
    isQuadrantOccupied,
    handleLogoClick,
    handleLogoDrag,
    handleLogoRemove,
    handleStageClick,
    createDragBoundFunc,
    createResizeBoundFunc,
    establishLogoHomeQuadrant,
    reestablishAllLogoHomeQuadrants,
  };
};

export const usePdfQuadrants = (options: PdfQuadrantsOptions = {}) => {
  const events = useQuadrantEvents(options);
  const state = useQuadrantState();
  const interactions = useQuadrantInteractions(state, events);

  const findQuadrantForLogo = (logoConfig: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => {
    return state.simpleQuadrants.value.find((quadrant) => {
      const logoCenter = {
        x: logoConfig.x + logoConfig.width / 2,
        y: logoConfig.y + logoConfig.height / 2,
      };
      return (
        logoCenter.x >= quadrant.x &&
        logoCenter.x <= quadrant.x + quadrant.width &&
        logoCenter.y >= quadrant.y &&
        logoCenter.y <= quadrant.y + quadrant.height
      );
    });
  };

  const canMoveToQuadrant = (
    image: KonvaNode,
    targetQuadrant: SimpleQuadrant,
  ) => {
    if (!targetQuadrant) return false;

    const currentOccupant = state.quadrantOccupancy.value.get(
      targetQuadrant.id.toString(),
    );
    return !currentOccupant || currentOccupant === image;
  };

  const getFittedImageDimensions = (
    imageBox: { width: number; height: number },
    quadrant: SimpleQuadrant,
  ) => {
    if (!quadrant.width || !quadrant.height) {
      return { width: imageBox.width, height: imageBox.height };
    }

    const imageAspectRatio = imageBox.width / imageBox.height;
    const quadrantAspectRatio = quadrant.width / quadrant.height;

    let fittedWidth: number;
    let fittedHeight: number;

    if (imageAspectRatio > quadrantAspectRatio) {
      fittedWidth = quadrant.width * 0.95;
      fittedHeight = fittedWidth / imageAspectRatio;
    } else {
      fittedHeight = quadrant.height * 0.95;
      fittedWidth = fittedHeight * imageAspectRatio;
    }

    return { width: fittedWidth, height: fittedHeight };
  };

  const isLogoWithinQuadrant = (
    logoBox: { x: number; y: number; width: number; height: number },
    quadrant: SimpleQuadrant,
  ) => {
    if (!quadrant) return false;

    return (
      logoBox.x >= quadrant.x &&
      logoBox.y >= quadrant.y &&
      logoBox.x + logoBox.width <= quadrant.x + quadrant.width &&
      logoBox.y + logoBox.height <= quadrant.y + quadrant.height
    );
  };

  const moveToQuadrant = (
    logoRect: { x: number; y: number; width: number; height: number },
    quadrant: SimpleQuadrant,
  ) => {
    const newX = Math.max(
      quadrant.x,
      Math.min(quadrant.x + quadrant.width - logoRect.width, logoRect.x),
    );
    const newY = Math.max(
      quadrant.y,
      Math.min(quadrant.y + quadrant.height - logoRect.height, logoRect.y),
    );

    return { x: newX, y: newY, width: logoRect.width, height: logoRect.height };
  };

  const isLogoInsideQuadrants = (logoBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): boolean => {
    if (state.simpleQuadrants.value.length === 0) {
      return false;
    }

    return state.simpleQuadrants.value.some((quadrant) =>
      isLogoWithinQuadrant(logoBox, quadrant),
    );
  };

  const loadLogoImage = (logoUrl: string) => {
    if (!logoUrl) {
      state.logoImageElement.value = null;
      state.logoImageLoaded.value = false;
      return;
    }

    state.logoImageLoaded.value = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.addEventListener('load', () => {
      state.logoImageElement.value = img;
      state.logoImageLoaded.value = true;
    });
    img.addEventListener('error', () => {
      state.logoImageElement.value = null;
      state.logoImageLoaded.value = false;
    });
    img.src = logoUrl;
  };

  const loadQuadrantsWhenReady = (loadOptions: LoadQuadrantsOptions) => {
    if (
      !loadOptions.propsQuadrants ||
      loadOptions.propsQuadrants.length === 0
    ) {
      return;
    }

    if (state.quadrantsScaled.value) {
      const currentPageData = state.quadrants.value.filter(
        (q) => q.page === loadOptions.currentPage,
      );

      state.simpleQuadrants.value = currentPageData.map((q, index) => ({
        id: q.id || `quadrant-${index}`,
        name: `quadrant-${q.page}-${q.id || index}`,
        x: q.x,
        y: q.y,
        width: q.width,
        height: q.height,
      }));

      events.emit('quadrants-changed');
      return;
    }

    const displayWidth = loadOptions.canvasWidth;
    const displayHeight = loadOptions.canvasHeight;

    if (
      loadOptions.originalPageWidth > 0 &&
      loadOptions.originalPageHeight > 0
    ) {
      const originalWidth = loadOptions.originalPageWidth;
      const originalHeight = loadOptions.originalPageHeight;
      const scaleX = displayWidth / originalWidth;
      const scaleY = displayHeight / originalHeight;

      state.quadrants.value = loadOptions.propsQuadrants.map(
        (q): PdfQuadrant => ({
          x: q.x * scaleX,
          y: q.y * scaleY,
          width: q.width * scaleX,
          height: q.height * scaleY,
          page: q.page || 1,
          id: q.id,
        }),
      );

      const currentPageData = state.quadrants.value.filter(
        (q) => q.page === loadOptions.currentPage,
      );

      state.simpleQuadrants.value = currentPageData.map((q, index) => ({
        id: q.id || `quadrant-${index}`,
        name: `quadrant-${q.page}-${q.id || index}`,
        x: q.x,
        y: q.y,
        width: q.width,
        height: q.height,
      }));

      state.quadrantsScaled.value = true;
      events.emit('quadrants-changed');
    } else {
      setTimeout(() => {
        if (
          loadOptions.originalPageWidth > 0 &&
          loadOptions.originalPageHeight > 0
        ) {
          loadQuadrantsWhenReady(loadOptions);
        }
      }, 50);
    }
  };

  const resetQuadrantsScaling = () => {
    state.quadrantsScaled.value = false;
  };

  const clearCustomization = () => {
    state.customerLogo.value = null;
    state.logoImageElement.value = null;
    state.logoImageLoaded.value = false;
    state.selectedLogoRef.value = null;
    state.isLogoSelected.value = false;
    state.quadrantOccupancy.value.clear();
    state.logoHomeQuadrants.value.clear();
  };

  const saveLogoStatesForPage = (page: number) => {
    if (!state.customerLogo.value) return;

    if (!state.logoStatesPerPage.value.has(page)) {
      state.logoStatesPerPage.value.set(page, new Map());
    }

    const pageStates = state.logoStatesPerPage.value.get(page)!;

    for (const [
      quadrantId,
      logoNode,
    ] of state.quadrantOccupancy.value.entries()) {
      if (logoNode) {
        const logoId = `logo_${quadrantId}_page_${page}`;
        const logoState: LogoState = {
          id: logoId,
          x: logoNode.x(),
          y: logoNode.y(),
          width: logoNode.width(),
          height: logoNode.height(),
          scaleX: logoNode.scaleX(),
          scaleY: logoNode.scaleY(),
          quadrantId,
          page,
        };

        pageStates.set(quadrantId.toString(), logoState);
      }
    }
  };

  const restoreLogoStatesForPage = (page: number, reestablishBounds = true) => {
    const pageStates = state.logoStatesPerPage.value.get(page);
    if (!pageStates) {
      return;
    }

    nextTick(() => {
      for (const [quadrantId, logoState] of pageStates.entries()) {
        const logoNode = state.quadrantOccupancy.value.get(quadrantId);
        if (logoNode) {
          (logoNode as any).x(logoState.x);
          (logoNode as any).y(logoState.y);
          (logoNode as any).width(logoState.width);
          (logoNode as any).height(logoState.height);
          (logoNode as any).scaleX(logoState.scaleX);
          (logoNode as any).scaleY(logoState.scaleY);

          const homeQuadrant = state.simpleQuadrants.value.find(
            (q) => q.id.toString() === quadrantId,
          );
          if (homeQuadrant) {
            state.logoHomeQuadrants.value.set(logoNode, homeQuadrant);
            state.quadrantOccupancy.value.set(quadrantId, logoNode);
          }
        }
      }

      const layer = [...state.quadrantOccupancy.value.values()][0]?.getLayer();
      layer?.batchDraw();
    });
  };

  const getCurrentLogoStates = (): LogoConfig[] => {
    if (!state.customerLogo.value || !state.logoImageElement.value) {
      return [];
    }

    const currentStates: LogoConfig[] = [];

    for (const [
      quadrantId,
      logoNode,
    ] of state.quadrantOccupancy.value.entries()) {
      if (logoNode) {
        const quadrantIdNum =
          typeof quadrantId === 'string'
            ? Number.parseInt(quadrantId, 10)
            : quadrantId;

        const currentX = logoNode.x();
        const currentY = logoNode.y();
        const currentWidth = logoNode.width() * logoNode.scaleX();
        const currentHeight = logoNode.height() * logoNode.scaleY();

        currentStates.push({
          x: currentX,
          y: currentY,
          width: currentWidth,
          height: currentHeight,
          image: state.logoImageElement.value,
          opacity: 0.9,
          quadrantId: quadrantIdNum,
        });
      }
    }

    return currentStates;
  };

  const getAllLogoStatesAcrossPages = (currentPage: number): LogoConfig[] => {
    if (!state.customerLogo.value || !state.logoImageElement.value) {
      return [];
    }

    const allStates: LogoConfig[] = [];
    const processedQuadrants = new Set<string>();

    for (const [page, pageStates] of state.logoStatesPerPage.value.entries()) {
      for (const [quadrantId, logoState] of pageStates.entries()) {
        const uniqueKey = `${quadrantId}_page_${page}`;

        if (!processedQuadrants.has(uniqueKey)) {
          allStates.push({
            x: logoState.x,
            y: logoState.y,
            width: logoState.width * logoState.scaleX,
            height: logoState.height * logoState.scaleY,
            image: state.logoImageElement.value,
            opacity: 0.9,
            quadrantId:
              typeof logoState.quadrantId === 'string'
                ? Number.parseInt(logoState.quadrantId, 10)
                : logoState.quadrantId,
            page: logoState.page,
          });
          processedQuadrants.add(uniqueKey);
        }
      }
    }

    if (state.quadrantOccupancy.value.size > 0) {
      for (const [
        quadrantId,
        logoNode,
      ] of state.quadrantOccupancy.value.entries()) {
        const uniqueKey = `${quadrantId}_page_${currentPage}`;

        if (!processedQuadrants.has(uniqueKey)) {
          const quadrantIdNum =
            typeof quadrantId === 'string'
              ? Number.parseInt(quadrantId, 10)
              : quadrantId;

          allStates.push({
            x: logoNode.x(),
            y: logoNode.y(),
            width: logoNode.width() * logoNode.scaleX(),
            height: logoNode.height() * logoNode.scaleY(),
            image: state.logoImageElement.value,
            opacity: 0.9,
            quadrantId: quadrantIdNum,
            page: currentPage,
          });
        }
      }
    }

    return allStates;
  };

  const restoreLogoStatesAndBounds = (page: number) => {
    restoreLogoStatesForPage(page, true);
  };

  return {
    getState: () => ({
      simpleQuadrants: state.simpleQuadrants,
      quadrants: state.quadrants,
      quadrantsScaled: state.quadrantsScaled,
      selectedLogoRef: state.selectedLogoRef,
      isLogoSelected: state.isLogoSelected,
      quadrantOccupancy: state.quadrantOccupancy,
      logoHomeQuadrants: state.logoHomeQuadrants,
      customerLogo: state.customerLogo,
      logoImageElement: state.logoImageElement,
      logoImageLoaded: state.logoImageLoaded,
      logoConfigs: state.logoConfigs,
      selectedLogoQuadrant: state.selectedLogoQuadrant,
    }),

    getInteractions: () => ({
      handleLogoClick: interactions.handleLogoClick,
      handleLogoDrag: interactions.handleLogoDrag,
      handleLogoRemove: interactions.handleLogoRemove,
      handleStageClick: interactions.handleStageClick,
      createDragBoundFunc: interactions.createDragBoundFunc,
      createResizeBoundFunc: interactions.createResizeBoundFunc,
      updateQuadrantOccupancy: interactions.updateQuadrantOccupancy,
      getLogoHomeQuadrant: interactions.getLogoHomeQuadrant,
      isQuadrantOccupied: interactions.isQuadrantOccupied,
      establishLogoHomeQuadrant: interactions.establishLogoHomeQuadrant,
      reestablishAllLogoHomeQuadrants:
        interactions.reestablishAllLogoHomeQuadrants,
    }),

    loadQuadrantsWhenReady,
    resetQuadrantsScaling,
    clearCustomization,
    loadLogoImage,

    getCurrentLogoStates,
    getAllLogoStatesAcrossPages,
    saveLogoStatesForPage,
    restoreLogoStatesForPage,
    restoreLogoStatesAndBounds,

    findQuadrantForLogo,
    canMoveToQuadrant,
    getFittedImageDimensions,
    isLogoWithinQuadrant,
    moveToQuadrant,
    isLogoInsideQuadrants,

    simpleQuadrants: state.simpleQuadrants,
    quadrants: state.quadrants,
    quadrantsScaled: state.quadrantsScaled,
    selectedLogoRef: state.selectedLogoRef,
    isLogoSelected: state.isLogoSelected,
    quadrantOccupancy: state.quadrantOccupancy,
    logoHomeQuadrants: state.logoHomeQuadrants,
    customerLogo: state.customerLogo,
    logoImageElement: state.logoImageElement,
    logoImageLoaded: state.logoImageLoaded,
    logoConfigs: state.logoConfigs,
    selectedLogoQuadrant: state.selectedLogoQuadrant,
  };
};
